
import { useCallback } from 'react';
import { useUserStorage } from './storageUtils';
import { CampaignFormData, Location, Creative } from '@/components/campaign/types';

interface CreativesByLocation {
  [locationId: string]: Creative[];
}

interface CreativesByMediaOwner {
  [mediaOwner: string]: Creative[];
}

/**
 * Consolidated hook for all campaign-related localStorage operations.
 * Provides type-safe, user-specific storage for campaign data.
 * Uses useUserStorage to automatically prefix keys with user identifier.
 */
export const useCampaignStorage = () => {
  const { getItem, setItem, removeItem } = useUserStorage();

  // Campaign name
  const getCampaignName = useCallback((): string => {
    return getItem('campaignName') || 'New Campaign';
  }, [getItem]);

  const setCampaignName = useCallback((name: string) => {
    setItem('campaignName', name);
  }, [setItem]);

  // Current step
  const getCurrentStep = useCallback((): number => {
    const step = getItem('currentStep');
    return step ? parseInt(step, 10) : 0;
  }, [getItem]);

  const setCurrentStep = useCallback((step: number) => {
    setItem('currentStep', step.toString());
  }, [setItem]);

  // Form data
  const getFormData = useCallback((): CampaignFormData | null => {
    const data = getItem('formData');
    if (!data) return null;
    try {
      return JSON.parse(data) as CampaignFormData;
    } catch {
      return null;
    }
  }, [getItem]);

  const setFormData = useCallback((data: CampaignFormData) => {
    setItem('formData', JSON.stringify(data));
    // Also save to legacy keys for backward compatibility during transition
    setItem('companyInfo', JSON.stringify(data));
    setItem('companyDetails', JSON.stringify(data));
  }, [setItem]);

  // Selected locations
  const getSelectedLocations = useCallback((): Location[] => {
    const data = getItem('selectedLocations');
    if (!data) return [];
    try {
      return JSON.parse(data) as Location[];
    } catch {
      return [];
    }
  }, [getItem]);

  const setSelectedLocations = useCallback((locations: Location[]) => {
    setItem('selectedLocations', JSON.stringify(locations));
  }, [setItem]);

  // Duration
  const getDuration = useCallback((): number => {
    const data = getItem('duration');
    return data ? parseInt(data, 10) : 14;
  }, [getItem]);

  const setDuration = useCallback((duration: number) => {
    setItem('duration', duration.toString());
  }, [setItem]);

  // Devices
  const getDevices = useCallback((): number => {
    const data = getItem('devices');
    return data ? parseInt(data, 10) : 0;
  }, [getItem]);

  const setDevices = useCallback((devices: number) => {
    setItem('devices', devices.toString());
  }, [setItem]);

  // Creatives
  const getCreatives = useCallback((): Creative[] => {
    const data = getItem('creatives');
    if (!data) return [];
    try {
      return JSON.parse(data) as Creative[];
    } catch {
      return [];
    }
  }, [getItem]);

  const setCreatives = useCallback((creatives: Creative[]) => {
    setItem('creatives', JSON.stringify(creatives));
    // Also update URLs for backward compatibility
    const urls = creatives.map(c => c.url);
    setItem('creativeUrls', JSON.stringify(urls));
  }, [setItem]);

  // Location-specific creatives
  const getLocationCreatives = useCallback((): CreativesByLocation => {
    const data = getItem('locationCreatives');
    if (!data) return {};
    try {
      return JSON.parse(data) as CreativesByLocation;
    } catch {
      return {};
    }
  }, [getItem]);

  const setLocationCreatives = useCallback((creatives: CreativesByLocation) => {
    setItem('locationCreatives', JSON.stringify(creatives));
  }, [setItem]);

  // Media owner-specific creatives
  const getMediaOwnerCreatives = useCallback((): CreativesByMediaOwner => {
    const data = getItem('mediaOwnerCreatives');
    if (!data) return {};
    try {
      return JSON.parse(data) as CreativesByMediaOwner;
    } catch {
      return {};
    }
  }, [getItem]);

  const setMediaOwnerCreatives = useCallback((creatives: CreativesByMediaOwner) => {
    setItem('mediaOwnerCreatives', JSON.stringify(creatives));
  }, [setItem]);

  // Creative preferences
  const getUseLocationSpecificCreatives = useCallback((): boolean => {
    const data = getItem('useLocationSpecificCreatives');
    return data === 'true';
  }, [getItem]);

  const setUseLocationSpecificCreatives = useCallback((value: boolean) => {
    setItem('useLocationSpecificCreatives', value.toString());
  }, [setItem]);

  const getUseMediaOwnerSpecificCreatives = useCallback((): boolean => {
    const data = getItem('useMediaOwnerSpecificCreatives');
    return data !== 'false'; // Default to true
  }, [getItem]);

  const setUseMediaOwnerSpecificCreatives = useCallback((value: boolean) => {
    setItem('useMediaOwnerSpecificCreatives', value.toString());
  }, [setItem]);

  // Draft and edit mode flags
  const getDraftDataLoaded = useCallback((): boolean => {
    return getItem('draftDataLoaded') === 'true';
  }, [getItem]);

  const setDraftDataLoaded = useCallback((value: boolean) => {
    setItem('draftDataLoaded', value.toString());
  }, [setItem]);

  const getPreserveCampaignId = useCallback((): boolean => {
    return getItem('preserveCampaignId') === 'true';
  }, [getItem]);

  const setPreserveCampaignId = useCallback((value: boolean) => {
    setItem('preserveCampaignId', value.toString());
  }, [setItem]);

  // Campaign ID
  const getCurrentCampaignId = useCallback((): string | null => {
    return getItem('currentCampaignId');
  }, [getItem]);

  const setCurrentCampaignId = useCallback((id: string) => {
    setItem('currentCampaignId', id);
  }, [setItem]);

  // Clear all campaign data
  const clearAllCampaignData = useCallback(() => {
    const keys = [
      'campaignName',
      'currentStep',
      'formData',
      'companyInfo',
      'companyDetails',
      'selectedLocations',
      'duration',
      'devices',
      'creatives',
      'creativeUrls',
      'locationCreatives',
      'mediaOwnerCreatives',
      'useLocationSpecificCreatives',
      'useMediaOwnerSpecificCreatives',
      'draftDataLoaded',
      'preserveCampaignId',
      'currentCampaignId'
    ];
    keys.forEach(key => removeItem(key));
  }, [removeItem]);

  return {
    // Campaign name
    getCampaignName,
    setCampaignName,
    
    // Navigation
    getCurrentStep,
    setCurrentStep,
    
    // Form data
    getFormData,
    setFormData,
    
    // Locations
    getSelectedLocations,
    setSelectedLocations,
    
    // Duration and devices
    getDuration,
    setDuration,
    getDevices,
    setDevices,
    
    // Creatives
    getCreatives,
    setCreatives,
    getLocationCreatives,
    setLocationCreatives,
    getMediaOwnerCreatives,
    setMediaOwnerCreatives,
    
    // Creative preferences
    getUseLocationSpecificCreatives,
    setUseLocationSpecificCreatives,
    getUseMediaOwnerSpecificCreatives,
    setUseMediaOwnerSpecificCreatives,
    
    // Mode flags
    getDraftDataLoaded,
    setDraftDataLoaded,
    getPreserveCampaignId,
    setPreserveCampaignId,
    
    // Campaign ID
    getCurrentCampaignId,
    setCurrentCampaignId,
    
    // Utilities
    clearAllCampaignData
  };
};
